import { useState } from "react";

interface ContactSectionProps {
  id?: string;
  showHeading?: boolean;
  className?: string;
}

const NAME_MIN_LENGTH = 2;
const MESSAGE_MIN_LENGTH = 20;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormValues {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const INITIAL_VALUES: FormValues = { name: "", email: "", subject: "", message: "" };

export default function ContactSection({ id, showHeading = true, className = "" }: ContactSectionProps) {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
  const [touched, setTouched] = useState<Partial<Record<keyof FormValues, boolean>>>({});

  const isValid =
    values.name.trim().length >= NAME_MIN_LENGTH &&
    EMAIL_PATTERN.test(values.email.trim()) &&
    values.subject.trim().length > 0 &&
    values.message.trim().length >= MESSAGE_MIN_LENGTH;

  const handleChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleBlur = (field: keyof FormValues) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ name: true, email: true, subject: true, message: true });
    if (!isValid || submitting) return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("success");
      form.reset();
      setValues(INITIAL_VALUES);
      setTouched({});
      return;
    }

    const text = Object.entries(values)
      .map(([key, value]) => `${key} - ${value}`)
      .join("\n");

    const payload = {
      email: values.email.trim(),
      text,
      problemType: values.subject.trim(),
      to_email: "support@trevicatech.com",
      subject: "Contact Form Submission",
    };

    setSubmitting(true);
    try {
      const response = await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus("success");
        form.reset();
        setValues(INITIAL_VALUES);
        setTouched({});
      } else {
        const text = await response.text();
        let parsed: { message?: string; detail?: string } = {};
        try { parsed = JSON.parse(text); } catch { /* noop */ }
        setErrorMsg(parsed?.message || parsed?.detail || "Something went wrong. Please try again.");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Network error. Please check your connection and try again.");
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id={id} className={`relative w-full bg-cream-100 py-24 md:py-32 border-y border-ink-950/5 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        {/* Heading */}
        {showHeading && (
          <div className="flex flex-col items-center text-center mb-14 md:mb-18">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-ink-700">
                Contact Us
              </span>
            </div>
            <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Let&rsquo;s build something
              <br />
              great together
            </h2>
            <p className="mt-4 max-w-lg text-sm md:text-base text-ink-700 leading-relaxed">
              Have a project in mind or a question? Reach out — we typically respond within 24 hours.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
          {/* Form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-cream-50 rounded-2xl border border-ink-950/10 p-7 md:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-widest font-medium text-ink-500 mb-2">
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={values.name}
                    onChange={handleChange("name")}
                    onBlur={handleBlur("name")}
                    placeholder="John Smith"
                    className="w-full px-4 py-3 rounded-xl bg-cream-100 border border-ink-950/10 text-sm text-ink-950 placeholder:text-ink-500/50 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                  {touched.name && values.name.trim().length < NAME_MIN_LENGTH && (
                    <p className="mt-1 text-[10px] text-red-600">
                      {values.name.trim().length === 0
                        ? "Full name is required."
                        : `Minimum ${NAME_MIN_LENGTH} characters.`}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-widest font-medium text-ink-500 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={values.email}
                    onChange={handleChange("email")}
                    onBlur={handleBlur("email")}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-xl bg-cream-100 border border-ink-950/10 text-sm text-ink-950 placeholder:text-ink-500/50 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                  {touched.email && !EMAIL_PATTERN.test(values.email.trim()) && (
                    <p className="mt-1 text-[10px] text-red-600">Enter a valid email address.</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-xs uppercase tracking-widest font-medium text-ink-500 mb-2">
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={values.subject}
                  onChange={handleChange("subject")}
                  onBlur={handleBlur("subject")}
                  placeholder="Project inquiry / Service question"
                  className="w-full px-4 py-3 rounded-xl bg-cream-100 border border-ink-950/10 text-sm text-ink-950 placeholder:text-ink-500/50 focus:outline-none focus:border-amber-400 transition-colors"
                />
                {touched.subject && values.subject.trim().length === 0 && (
                  <p className="mt-1 text-[10px] text-red-600">Subject is required.</p>
                )}
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-xs uppercase tracking-widest font-medium text-ink-500 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={values.message}
                  onChange={handleChange("message")}
                  onBlur={handleBlur("message")}
                  placeholder="Tell us about your project, goals, and how we can help..."
                  className="w-full px-4 py-3 rounded-xl bg-cream-100 border border-ink-950/10 text-sm text-ink-950 placeholder:text-ink-500/50 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                ></textarea>
                {touched.message && values.message.trim().length < MESSAGE_MIN_LENGTH ? (
                  <p className="mt-1 text-[10px] text-red-600">
                    Minimum {MESSAGE_MIN_LENGTH} characters ({values.message.trim().length}/{MESSAGE_MIN_LENGTH})
                  </p>
                ) : (
                  <p className="mt-1 text-[10px] text-ink-500">Minimum 20, maximum 500 characters</p>
                )}
              </div>

              {/* Honeypot */}
              <input
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none h-0 w-0"
              />

              <button
                type="submit"
                disabled={!isValid || submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/30 disabled:text-ink-950/40 disabled:cursor-not-allowed text-ink-950 px-6 py-3.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {submitting ? "Sending..." : "Send message"}
                <i className="ri-send-plane-line text-base"></i>
              </button>

              {status === "success" && (
                <div className="mt-4 p-4 rounded-xl bg-moss-100 border border-moss-200 text-sm text-moss-700">
                  Thank you for your message! We&rsquo;ll get back to you shortly.
                </div>
              )}
              {status === "error" && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                  {errorMsg}
                </div>
              )}
            </form>
          </div>

          {/* Contact info sidebar */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div className="bg-cream-50 rounded-2xl border border-ink-950/10 p-7 md:p-9">
              <h3 className="font-display font-bold text-xl text-ink-950 mb-6">
                Contact Information
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
                    <i className="ri-map-pin-line text-amber-600"></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-ink-500 font-medium mb-1">Address</p>
                    <p className="text-sm text-ink-700 leading-relaxed">
                      307 Cotton Exchange<br />
                      Old Hall Street<br />
                      Liverpool, L3 9LQ<br />
                      United Kingdom
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
                    <i className="ri-mail-line text-amber-600"></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-ink-500 font-medium mb-1">Email</p>
                    <a href="mailto:support@trevicatech.com" className="text-sm text-ink-700 hover:text-amber-600 transition-colors cursor-pointer">
                      support@trevicatech.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
                    <i className="ri-phone-line text-amber-600"></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-ink-500 font-medium mb-1">Phone</p>
                    <a href="tel:+447451257027" className="text-sm text-ink-700 hover:text-amber-600 transition-colors cursor-pointer">
                      +44 7451 257027
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-cream-50 rounded-2xl border border-ink-950/10 p-7 md:p-9">
              <h3 className="font-display font-bold text-xl text-ink-950 mb-4">
                Why work with us?
              </h3>
              <ul className="space-y-3">
                {[
                  "Experienced cross-functional team",
                  "Full-cycle — from idea to scaling",
                  "Transparent communication",
                  "Measurable, data-driven results",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-ink-700">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-moss-100 shrink-0 mt-0.5">
                      <i className="ri-check-line text-[10px] text-moss-600"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}